import { useState, useCallback, useEffect } from "react";
import {
  Form,
  Button,
  Select,
  Input,
  Table,
  message,
  Card,
  Space,
  Tooltip,
  Checkbox,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { DownloadOutlined } from "@ant-design/icons";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";

import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import { setMenu } from "../../redux/reducers/navigationSlice";

import ResidentAPI from "../../api/ResidentAPI";
import LocationAPI from "../../api/LocationAPI";

import { sortAlphabetically } from "../../utils/tableUtils";

import "./resident-maintenance.styles.css";

const initialFormValues = {
  id: "",
  cedula: "",
  fullName: "",
  gender: "",
  locationId: "",
  building: "",
  phone: "",
  isPrimary: false,
};

const initialSearchFormValues = {
  cedula: "",
  fullName: "",
  gender: "",
  locationId: "",
  building: "",
  phone: "",
  isPrimary: "",
};

const ResidentMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [searchLocationForm] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [locationListData, setLocationListData] = useState({
    locations: [],
    totalCount: 0,
  });
  const [residentListData, setResidentListData] = useState({
    residents: [],
    totalCount: 0,
  });
  const [locationModalIsOpen, setLocationModalIsOpen] = useState(false);
  const [searchLocationIsLoading, setSearchLocationIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isSelected, setIsSelected] = useState(false);
  const [residentListIsLoading, setResidentListIsLoading] = useState(false);

  const renderSelectLocationButton = (_, record) => (
    <Button
      onClick={() => {
        form.setFieldValue("locationId", record.key);
        form.setFieldValue("building", record.Building);
        setLocationModalIsOpen(false);
      }}
      type="primary"
    >
      Seleccionar
    </Button>
  );

  const listColumns = [
    {
      title: "Cédula",
      dataIndex: "Cedula",
      sorter: (a, b) => sortAlphabetically(a, b, "Cedula"),
    },
    {
      title: "Nombre",
      dataIndex: "FullName",
      sorter: (a, b) => sortAlphabetically(a, b, "FullName"),
    },
    {
      title: "Género",
      dataIndex: "Gender",
      sorter: (a, b) => sortAlphabetically(a, b, "FullName"),
    },
    {
      title: "Edificio",
      dataIndex: "Building",
      sorter: (a, b) => sortAlphabetically(a, b, "FullName"),
    },
    {
      title: "Teléfono",
      dataIndex: "Phone",
    },
    {
      title: "Primario",
      dataIndex: "IsPrimary",
    },
    {
      title: "Opción",
      dataIndex: "view",
      render: (_, { Id }) => (
        <>
          <Button onClick={() => loadSelectedResident(Id)}>Seleccionar</Button>
        </>
      ),
    },
  ];

  const locationTableColumns = [
    { title: "Id", dataIndex: "key", hidden: true },
    {
      title: "Vivienda",
      dataIndex: "Building",
    },
    {
      title: "Creación",
      dataIndex: "CreatedDate",
    },
    {
      title: "Opción",
      dataIndex: "select",
      render: renderSelectLocationButton,
    },
  ];

  const clearResidentForm = () => {
    setIsSelected(false);
    form.resetFields();
    loadResidentList();
  };

  const loadLocationList = async () => {
    try {
      setSearchLocationIsLoading(true);
      const response = await LocationAPI.getLocations(
        token,
        searchLocationForm.getFieldsValue()
      );

      const { locations, totalCount } = response.data;

      setLocationListData({ locations, totalCount });
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      setSearchLocationIsLoading(false);
    }
  };

  const loadInitialData = useCallback(async () => {
    setResidentListIsLoading(true);

    try {
      const response = await ResidentAPI.getResidents(token);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const { residents, totalCount } = response.data;
      setResidentListData({ residents, totalCount });
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      setResidentListIsLoading(false);
    }
  }, [messageApi, dispatch, token]);

  const loadResidentList = async (activePage = 1, activePageSize = 10) => {
    setResidentListIsLoading(true);

    try {
      const response = await ResidentAPI.getResidents(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const { residents, totalCount } = response.data;

      setCurrentPage(activePage);
      setResidentListData({ residents, totalCount });
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      setResidentListIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setMenu("residentMaintenance"));
    loadInitialData();
  }, [loadInitialData, dispatch]);

  const onFinishFormResident = async (values) => {
    if (typeof values.isPrimary == "boolean")
      values.IsPrimary = values.IsPrimary ? 1 : 0;

    messageApi.open({ type: "loading", content: "Guardando" });

    try {
      let response = null;

      if (isSelected) {
        response = await ResidentAPI.updateResident(token, values);
      } else {
        response = await ResidentAPI.createResident(token, values);
      }

      messageApi.destroy();
      messageApi.open({
        type: response.data.success ? "success" : "error",
        content: response.data.message,
      });
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.destroy();
      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      clearResidentForm();
    }
  };

  const loadSelectedResident = async (id) => {
    try {
      const response = await ResidentAPI.getResident(token, id);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const {
        Id,
        Cedula,
        Name,
        Surname,
        Gender,
        LocationId,
        Building,
        Phone,
        IsPrimary,
      } = response.data.resident;

      form.setFieldValue("id", Id);
      form.setFieldValue("cedula", Cedula);
      form.setFieldValue("name", Name);
      form.setFieldValue("surname", Surname);
      form.setFieldValue("gender", Gender);
      form.setFieldValue("locationId", LocationId);
      form.setFieldValue("building", Building);
      form.setFieldValue("phone", Phone);
      form.setFieldValue("isPrimary", IsPrimary);
      setIsSelected(true);
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde.";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  const onClickDownloadButton = async () => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });

      const response = await ResidentAPI.downloadResidents(token, {
        ...searchForm.getFieldsValue(),
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `Residentes ${moment().format("DD-MM-YYYY")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde.";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  };

  return (
    <div className="resident-maintenance">
      <div>{messageContextHolder}</div>
      <Modal
        title="Seleccionar Residencia"
        open={locationModalIsOpen}
        onCancel={() => setLocationModalIsOpen(false)}
        footer={null}
      >
        <Form
          form={searchLocationForm}
          onFinish={() => loadLocationList()}
          layout="inline"
        >
          <Form.Item label="Número" name="building">
            <Input />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit">Buscar</Button>
          </Form.Item>
        </Form>
        <Table
          style={{ marginTop: "10px" }}
          columns={locationTableColumns}
          dataSource={locationListData.locations}
          pagination={false}
          scroll={{ y: 300 }}
          loading={searchLocationIsLoading}
        />
      </Modal>
      <div className="resident-flex-container">
        <Card
          title={`${isSelected ? "Actualizar" : "Registrar"} Residente`}
          className="resident-form-container"
        >
          <Form
            form={form}
            initialValues={initialFormValues}
            onFinish={onFinishFormResident}
          >
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              label="Cédula"
              name="cedula"
              style={{ width: "220px" }}
              required
            >
              <Input required />
            </Form.Item>
            <Form.Item label="Nombres" name="name" required>
              <Input required />
            </Form.Item>
            <Form.Item label="Apellidos" name="surname" required>
              <Input required />
            </Form.Item>
            <Form.Item
              label="Género"
              name="gender"
              style={{ width: "230px" }}
              required
            >
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                <Select.Option value="M">Masculino</Select.Option>
                <Select.Option value="F">Femenino</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="locationId" hidden>
              <Input required />
            </Form.Item>
            <Space.Compact>
              <Tooltip title="Debe presionar 'Seleccionar'">
                <Form.Item label="Residencia" name="building" required>
                  <Input readOnly required />
                </Form.Item>
              </Tooltip>
              <Button
                htmlType="button"
                onClick={async () => {
                  setLocationModalIsOpen(true);
                  await loadLocationList();
                }}
              >
                Seleccionar
              </Button>
            </Space.Compact>
            <Form.Item
              label="Teléfono"
              name="phone"
              style={{ width: "250px" }}
              required
            >
              <Input type="phone" required />
            </Form.Item>
            <Form.Item name="isPrimary" valuePropName="checked">
              <Checkbox>Primario</Checkbox>
            </Form.Item>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Button type="primary" htmlType="submit">
                Guardar
              </Button>
              <Button htmlType="button" onClick={clearResidentForm}>
                Limpiar
              </Button>
            </div>
          </Form>
        </Card>
        <div className="resident-list-container">
          <Form
            initialValues={initialSearchFormValues}
            form={searchForm}
            onFinish={() => loadResidentList()}
            layout="vertical"
            style={{ display: "flex", gap: "10px" }}
          >
            <Form.Item label="Cédula" name="cedula">
              <Input />
            </Form.Item>
            <Form.Item label="Nombre" name="fullName">
              <Input />
            </Form.Item>
            <Form.Item label="Género" name="gender" style={{ width: "150px" }}>
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                <Select.Option value="M">Masculino</Select.Option>
                <Select.Option value="F">Femenino</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Edificio" name="building">
              <Input placeholder="Exacto" />
            </Form.Item>
            <Form.Item
              label="Primario"
              name="isPrimary"
              style={{ width: "150px" }}
            >
              <Select>
                <Select.Option value="">-- Seleccionar --</Select.Option>
                <Select.Option value="1">Si</Select.Option>
                <Select.Option value="0">No</Select.Option>
              </Select>
            </Form.Item>
            <FormButtonsWrap>
              <Button type="primary" htmlType="submit">
                Buscar
              </Button>
              <Button
                htmlType="button"
                onClick={onClickDownloadButton}
                icon={<DownloadOutlined />}
              >
                Descargar
              </Button>
              <Button
                onClick={async () => {
                  searchForm.resetFields();
                  await loadResidentList();
                }}
              >
                Limpiar
              </Button>
            </FormButtonsWrap>
          </Form>
          <DataTotalDisplay
            currentCount={residentListData.residents.length}
            totalCount={residentListData.totalCount}
          />
          <Table
            loading={residentListIsLoading}
            columns={listColumns}
            dataSource={residentListData.residents}
            size="small"
            pagination={{
              current: currentPage,
              pageSize: currentPageSize,
              total: residentListData.totalCount,
            }}
            onChange={(e) => {
              loadResidentList(e.current, e.pageSize);
              setCurrentPage(e.current);
              setCurrentPageSize(e.pageSize);
            }}
            scroll={{ y: "360px" }}
          ></Table>
        </div>
      </div>
    </div>
  );
};

export default ResidentMaintenancePage;
